import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/firestore'
import 'firebase/storage'
import Cookies from 'js-cookie'
import _ from 'lodash'

import subscriptionPlans from '../fixtures/subscriptionPlans.json'
import { detectLang, gtagEvent } from '../lib/helpers'
import { logError } from '../lib/monitoring/safeErrorLog'
import measurementHelper from './apiHelpers/measurementHelper'

const firestore = firebase.firestore

// Must match doesNotChangeProtectedUserFields in firestore.rules
const SERVER_MANAGED_USER_FIELDS = [
  'admin',
  'staffAccount',
  'platform',
  'subscription',
  'billingPlatform',
  'email'
]

class FirebaseHandler {
  user = null
  setUserData = null
  sentRegistration = false

  /**
   * Registers the user with given details
   */
  registerUser = (email, password) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(
          (data) => {
            if (data.user?.uid) {
              this.addNewUserToFirestore(data.user).then((userData) => {
                this.afterLogin(userData).then(() => {
                  resolve(data.user)
                })
              })
            }
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  /**
   * Login user with given details
   */
  loginUser = (email, password) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(
          async ({ user }) => {
            const userData = await this.getUserData(user.uid)
            await this.afterLogin(userData)
            resolve(user)
          },
          (error) => {
            reject(this._handleError(error))
          }
        )
    })
  }

  loginWithOAuth = (customToken) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signInWithCustomToken(customToken)
        .then(
          async ({ user }) => {
            const userData = await this.getUserData(user.uid)
            if (userData) await this.afterLogin(userData, true)
            resolve(user)
          },
          (error) => {
            reject(this._handleError(error))
          }
        )
    })
  }

  //For impersonate
  loginWithCustomToken = (customToken) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signInWithCustomToken(customToken)
        .then(
          async ({ user }) => {
            resolve(user)
          },
          (error) => {
            reject(this._handleError(error))
          }
        )
    })
  }

  afterLogin = async (userData, oauth) => {
    try {
      const currentLang = this.getCurrentLanguage()
      //const currentPlan = await this.getCurrentPlan(userData)

      const clientId = Cookies.get('_ga_id_only') ?? ''
      this.updateUserField(userData.userId, { gaClientId: clientId }).then(
        () => {
          const platform = userData.platform
            ? userData.platform.name
            : 'generic'
          if (this.isRegistration(userData)) {
            measurementHelper.send({
              google: {
                clientId,
                userId: userData.userId,
                events: [{ name: 'sign_up', params: { method: platform } }]
              }
            })
          } else {
            measurementHelper.send({
              google: {
                clientId,
                userId: userData.userId,
                events: [{ name: 'login', params: { method: platform } }]
              }
            })
          }
        }
      )
    } catch (e) {
      logError('[afterLogin] Failed', e)
    }
  }

  /**
   * forget Password user with given details
   */
  forgetPassword = (email) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .sendPasswordResetEmail(email)
        .then(() => {
          resolve(true)
        })
        .catch((error) => {
          reject(this._handleError(error))
        })
    })
  }

  removeAuthCookies = async () => {
    fetch('/api/logout').catch((e) => {
      logError('[removeAuthCookies] Failed to log out', e)
    })
  }
  unsubscribeSnapshot = async () => {
    this.listening = false
    if (this.unsubscribe) await this.unsubscribe()
  }
  /*
   * Logout the user
   */
  logout = () => {
    return new Promise(async (resolve, reject) => {
      await this.unsubscribeSnapshot()
      //await this.removeAuthCookies()
      firebase
        .auth()
        .signOut()
        .then(async () => {
          resolve(true)
        })
        .catch((error) => {
          reject(this._handleError(error))
        })
    })
  }

  /**
   * Social Login user with given details
   */
  socialLoginUser = (data, type) => {
    let credential = {}
    if (type === 'google') {
      credential = firebase.auth.GoogleAuthProvider.credential(
        data.idToken,
        data.token
      )
    } else if (type === 'facebook') {
      credential = firebase.auth.FacebookAuthProvider.credential(data.token)
    }
    return new Promise((resolve, reject) => {
      if (!!credential) {
        firebase
          .auth()
          .signInWithCredential(credential)
          .then((user) => {
            resolve(this.addNewUserToFirestore(user))
          })
          .catch((error) => {
            reject(this._handleError(error))
          })
      } else {
        reject(this._handleError(error))
      }
    })
  }

  addNewUserToFirestore = async (user) => {
    const collection = firestore().collection('users')

    const details = {
      userId: user.uid,
      firstName: '',
      lastName: '',
      fullName: user.email ?? '',
      email: user.email,
      createdDtm: firestore.FieldValue.serverTimestamp(),
      lists: [{ name: 'Default', id: uniqueId() }],
      coupons: [{ name: '10% off', id: uniqueId() }],
      uploadedImages: []
    }

    const data = { ...details }

    await collection.doc(user.uid).set(data)

    return data
  }

  setLoggeedInUser = (user, userData) => {
    localStorage.setItem('authUser', JSON.stringify(user))
    localStorage.setItem('userData', JSON.stringify(userData))
  }

  /**
   * Returns the authenticated user
   */
  getAuthenticatedUser = () => {
    if (!localStorage.getItem('authUser')) return null
    return JSON.parse(localStorage.getItem('authUser'))
  }

  getDisplays = async () => {
    const query = await firestore().collection('popups')
    const snapshot = await query.where('userId', '==', this.user.id).get()

    let displays = []
    snapshot.forEach((doc, index) => {
      const data = doc.data()
      //Skip items left over from the removed A/B test feature
      if (!data.abTest) displays.push({ ...data, id: doc.id })
    })

    return displays
  }

  getDisplay = async (id) => {
    const docRef = firestore().collection('popups').doc(id)
    const doc = await docRef.get()
    if (!doc.exists) return null
    return { ...doc.data(), id: id }
  }

  //Add new display
  addDisplay = async (display, userData) => {
    const docRef = await firestore().collection('popups').add(display)
    try {
      gtagEvent({
        action: 'displayInteraction',
        params: {
          display_type: display.type,
          action: 'create'
        }
      })
    } catch (e) {
      logError('[addDisplay] Failed to send the gtag event', e)
    }

    return docRef.id
  }

  updateDisplay = async (id, display) => {
    const docRef = firestore().collection('popups').doc(id)
    await docRef.update(display)

    await this.purgeCache()
    gtagEvent({
      action: 'displayInteraction',
      params: {
        display_type: display.type,
        action: 'edit'
      }
    })

    return true
  }

  //This is for displays
  updateField = async (id, field, value) => {
    const docRef = firestore().collection('popups').doc(id)
    await docRef.update({ [field]: value })
    await this.purgeCache()
    return true
  }

  //This is for users
  updateUserField = async (userId, updates) => {
    const docRef = firestore().collection('users').doc(userId)
    await docRef.set(updates, { merge: true })
    return true
  }

  purgeCache = async () => {
    const scriptHost = process.env.NEXT_PUBLIC_SCRIPT_HOST
    const deleteDisplayCache = `${scriptHost}/delete-redis-cache/${this.user.id}`
    try {
      await axios.post(deleteDisplayCache, {})
    } catch (e) {
      logError('[purgeCache] Failed to delete the display cache', e)
    }
  }

  onFinishInstruction = async () => {
    try {
      await this.updateUserField(this.user.id, { finishedInstruction: true })
      this.setUserData?.((userData) => {
        return { ...userData, finishedInstruction: true }
      })
    } catch (e) {
      logError('[onFinishInstruction] Failed to update', e)
    }
  }

  previewScreenShot = (display) => {
    let newDisp = {}
    const removeKeys = [
      'title',
      'enterEmailInto',
      'thankYouContents',
      'triggersWhen',
      'shouldTheDisplayRecur',
      'showToUsersWho',
      'whenToShow',
      'followUpEmail',
      'enterEmailInto',
      'tags',
      'createdAt',
      'updatedAt',
      'afterConverting',
      'thumbnail',
      'showPromolayerLogo',
      'webhookInfo',
      'showOnPages',
      'DontShowOnPages',
      'userId',
      'published',
      'publishedBefore',
      'effect'
    ]
    for (const key in display) {
      if (!removeKeys.includes(key)) {
        newDisp[key] = display[key]
      }
    }

    newDisp.showPromolayerLogo = false
    newDisp.forms = newDisp.forms.filter((form) => form.enabled)

    delete newDisp.font.variants
    delete newDisp.font.files

    const jsonDisplay = encodeURIComponent(JSON.stringify(newDisp))

    const url = `https://d.app.promolayer.io/preview?display=${jsonDisplay}`
    window.open(url, '_blank')
  }

  deleteDisplay = async (display) => {
    try {
      const docRef = firestore().collection('popups').doc(display.id)
      await docRef.delete()
      gtagEvent({
        action: 'displayInteraction',
        params: {
          display_type: display.type,
          action: 'delete'
        }
      })
      await this.purgeCache()
      return [true, null]
    } catch (e) {
      return [null, e]
    }
  }

  duplicateDisplay = async (id, defaults = null, titleChange = true) => {
    try {
      let data = await this.getDisplay(id)

      if (titleChange) data.title += ' (Copy)'

      data.published = false

      if (defaults) {
        data = { ...data, ...defaults }
      }
      delete data.id
      const duplicatedRef = await firestore().collection('popups').add(data)
      return [{ ...data, id: duplicatedRef.id }, null]
    } catch (e) {
      return [null, e]
    }
  }

  uploadImage = async (dataUrl, oldUrl) => {
    const storage = await firebase.storage()
    const storageRef = storage.ref()
    const imageRef = storageRef.child('images')

    let fileName = ''
    if (oldUrl && /^https:\/\/firebasestorage/.test(oldUrl)) {
      fileName = this.getFileNameFromUrl(oldUrl) || uniqueId()
    } else {
      fileName = uniqueId()
    }

    const photoRef = imageRef.child(fileName)

    return await photoRef.putString(dataUrl, 'data_url')
  }

  uploadUserFile = async (file) => {
    const storage = await firebase.storage()
    const storageRef = storage.ref()

    const extension = getFileExtension(file)
    const fileName = extension ? `${uniqueId()}.${extension}` : uniqueId()
    const imageRef = storageRef.child(`uploaded/${this.user.id}/${fileName}`)

    return await imageRef.put(file)
  }

  getEmailTemplates = async (userData) => {
    const query = firestore().collection('emailTemplates')
    const snapshot = await query.where('userId', '==', userData.userId).get()
    let templates = []
    snapshot.forEach((doc, index) => {
      templates.push({ id: doc.id, ...doc.data() })
    })

    return _.orderBy(templates, ['name'], ['asc'])
  }

  getEmailTemplate = async (id) => {
    const query = firestore().collection('emailTemplates')
    const docRef = await query.doc(id).get()
    return docRef.data()
  }

  saveEmailTemplate = async (data, id) => {
    data.userId = this.user.id
    const query = await firestore().collection('emailTemplates')
    if (id) {
      const ref = query.doc(id)
      return ref.set(data, { merge: true })
    } else {
      return query.add(data)
    }
  }

  deleteEmailTemplate = async (id) => {
    const ref = await firestore().collection('emailTemplates').doc(id)
    return ref.delete()
  }

  getUserDataDocRef = async (userId) => {
    userId = userId || this.user.id
    const query = await firestore().collection('users')
    return query.doc(userId)
  }

  getTestSubscription = async () => {
    const docRef = await firestore()
      .collection('users')
      .doc('bsUrEZaeZRe2x3ACLQS4gt7i9O42')
    const doc = await docRef.get()
    return await doc.data().subscription
  }
  getUserData = async (userId) => {
    userId = userId || this.user.id
    const docRef = await this.getUserDataDocRef(userId)
    const doc = await docRef.get()
    const data = await doc?.data()

    // if (!data) {
    //   const {details} = this.addNewUserToFirestore(this.user)
    //   return details
    // }
    return data
  }

  listenUserDataChanges = (userId, callback) => {
    if (!this.listening) {
      this.listening = true
      this.unsubscribe = firestore()
        .collection('users')
        .where('userId', '==', userId)
        .onSnapshot((querySnapshot) => {
          querySnapshot.docChanges().forEach((change) => {
            if (change.type === 'modified') {
              callback(change.doc.data())
            } else if (change.type === 'removed') {
              callback(false)
            }
          })
          //callback()
        })
      return this.unsubscribe
    }
  }

  //This is called when every time user logged in
  updateUserData = async (userData) => {
    const query = await firestore().collection('users')
    const doc = await query.doc(userData.userId)
    // These fields are written only by the server, and the local copy may be stale
    await doc.set(_.omit(userData, SERVER_MANAGED_USER_FIELDS), { merge: true })

    this.updateUserDataState(userData)
  }

  getCurrentLanguage = () => {
    return detectLang()
  }

  updateUserDataState = (userData) => {
    if (this.setUserData) this.setUserData(_.cloneDeep(userData))
  }

  deleteImage = async (url) => {
    const storage = await firebase.storage()
    const storageRef = storage.ref()
    const fileName = this.getFileNameFromUrl(url)

    if (fileName) {
      const fileRef = storageRef.child(`uploaded/${this.user.id}/${fileName}`)

      // Delete the file
      await fileRef.delete()
    } else {
      throw new Error('File not found')
    }
  }

  getCouponHistories = async (id, allOfUsers) => {
    const query = await firestore().collection('couponHistories')
    let snapshot
    if (allOfUsers) {
      snapshot = await query.where('userId', '==', id).get()
    } else {
      snapshot = await query
        .where('displayId', '==', id)
        .where('userId', '==', this.user.id)
        .get()
    }

    const list = []
    snapshot.forEach((doc) => {
      list.push({ id: doc.id, ...doc.data() })
    })
    return list
  }

  deleteCouponHistories = async (checked) => {
    const ids = [...checked]
    const deleteRecursive = async () => {
      const tenIds = ids.splice(0, 10)

      const snapshot = await firestore()
        .collection('couponHistories')
        .where('userId', '==', this.user.id)
        .where(firestore.FieldPath.documentId(), 'in', tenIds)
        .get()

      if (!snapshot.empty) {
        snapshot.forEach((doc, index) => {
          doc.ref.delete()
        })
      }

      if (ids.length > 0) return await deleteRecursive()
    }
    await deleteRecursive()
  }

  addEmailList = async (list, userData) => {
    list.id = uniqueId()
    userData.lists.push(list)
    await this.updateUserData(userData)
  }

  deleteEmailListItems = async (listId, checked) => {
    const ids = [...checked]
    const deleteRecursive = async () => {
      const tenIds = ids.splice(0, 10)

      const snapshot = await firestore()
        .collection('emailList')
        .where('userId', '==', this.user.id)
        .where(firestore.FieldPath.documentId(), 'in', tenIds)
        .get()

      if (!snapshot.empty) {
        snapshot.forEach((doc, index) => {
          doc.ref.delete()
        })
      }

      if (ids.length > 0) return await deleteRecursive()
    }
    await deleteRecursive()
  }

  insertToEmailList = async (data) => {
    const query = await firestore().collection('emailList')
    await query.add(data)
  }

  deleteEmailList = async (listId, userData) => {
    const index = userData.lists.findIndex((item) => item.id === listId)
    userData.lists.splice(index, 1)
    const query = await firestore().collection('emailList')
    const snapshot = await query
      .where('userId', '==', this.user.id)
      .where('listId', '==', listId)
      .get()
    snapshot.forEach((doc, index) => {
      doc.ref.delete()
    })
    await this.updateUserData(userData)
  }

  getUserEmailList = async (listId) => {
    const query = await firestore().collection('emailList')
    const snapshot = await query
      .where('userId', '==', this.user.id)
      .where('listId', '==', listId)
      .get()
    let list = []
    snapshot.forEach((doc, index) => {
      list.push({ id: doc.id, ...doc.data() })
    })
    return list
  }

  getUserFileList = async () => {
    const storage = await firebase.storage()
    const storageRef = storage.ref()
    const fileRef = storageRef.child('uploaded/' + this.user.id)
    return await fileRef.listAll()
  }

  getCurrentPlan = async (userData) => {
    try {
      if (userData.platform?.name === 'wix') {
        if (userData.platform.currentPlan === 'Free') {
          const plans = await this.getSubscriptionPlans('free')
          return plans[0]
        } else {
          const plans = await this.getSubscriptionPlans(userData.platform.name)
          return plans.find((item) =>
            Object.values(item).includes(userData.platform.currentPlan)
          )
        }
      } else {
        if (userData?.subscription?.price_id) {
          const plans = await this.getSubscriptionPlans(
            userData.billingPlatform?.platform ?? 'stripe'
          )
          return plans.find((item) =>
            Object.values(item).includes(userData.subscription.price_id)
          )
        } else {
          const plans = await this.getSubscriptionPlans('free')
          return plans[0] //
        }
      }
    } catch (e) {
      return {
        planviews: 100,
        planname: 'Free',
        includesbranding: false
      }
    }
  }

  getSubscriptionPlans = async (platform, withFree) => {
    let plans = subscriptionPlans.filter((plan) => plan.platform === platform)
    if (withFree) {
      plans.push(await this.getFreePlan())
    }
    return _.orderBy(plans, ['planviews'], ['asc'])
  }

  getFreePlan = async () => {
    return subscriptionPlans.find((plan) => plan.platform === 'free')
  }

  getFileNameFromUrl = (url) => {
    if (/^https:\/\/firebasestorage/.test(url)) {
      const regex = new RegExp(
        /firebasestorage.googleapis.com\/(.*%2F){2}([^?]*)/
      )
      const res = regex.exec(url)
      return res && res[2]
    }
    return null
  }

  getTemplateThumbnail = async (id) => {
    const storage = await firebase.storage()
    const storageRef = await storage.ref(`templateThumbnails/${id}.jpg`)
    return storageRef.getDownloadURL()
  }

  setUserHandler = (user, setUserData) => {
    this.user = user
    this.setUserData = setUserData
  }

  isRegistration = (data) => {
    try {
      let createdAt
      if (data.createdDtm.seconds) {
        createdAt = new Date(data.createdDtm.seconds * 1000)
      } else {
        createdAt = data.createdDtm.toDate()
      }

      return Date.now() - createdAt.getTime() < 1000 * 30
    } catch (e) {
      return true
    }
  }

  getCreatedAt = (createdDtm) => {
    try {
      let createdAt
      if (createdDtm.seconds) {
        createdAt = new Date(createdDtm.seconds * 1000)
      } else {
        createdAt = createdDtm.toDate()
      }

      return createdAt
    } catch (e) {
      return new Date()
    }
  }

  /**
   * Handle the error
   * @param {*} error
   */
  _handleError(error) {
    // var errorCode = error.code;
    var errorMessage = error.message
    return errorMessage
  }
}

let _fireBaseHandler = null

/**
 * Returns the firebase backend
 */
const getFirebaseHandler = () => {
  if (!_fireBaseHandler) {
    _fireBaseHandler = new FirebaseHandler()
  }
  return _fireBaseHandler
}

/**
 * Returns unique ID
 */
export const uniqueId = function () {
  return (
    '_' + Date.now().toString(32) + Math.random().toString(36).substr(2, 10)
  )
}

/**
 * Returns the file extension from its name, falling back to its MIME type
 */
const getFileExtension = (file) => {
  const match = /\.([a-z0-9]+)$/i.exec(file?.name ?? '')
  if (match) return match[1].toLowerCase()

  const subtype = file?.type?.split('/')[1]
  return subtype ? subtype.split('+')[0].toLowerCase() : ''
}

export { getFirebaseHandler }
